import { useEffect, useMemo, useState } from 'react';
import { Button, Textarea } from '@tark/shared/components';
import type { CompetitionOrganizerEntry } from '@tark/shared/lib';
import { AlertTriangle, LockKeyhole, X } from 'lucide-react';
import type { OrganizerDialogSubmission, OrganizerEntryIntent } from './organizerCompetitionUi';
import { uniqueLinkedEntrants } from './organizerCompetitionUi';

type OrganizerActionDialogProps = {
  intent: OrganizerEntryIntent | null;
  entries: CompetitionOrganizerEntry[];
  isPending: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (submission: OrganizerDialogSubmission) => void;
};

const selectClassName =
  'h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/30';

export default function OrganizerActionDialog({
  intent,
  entries,
  isPending,
  errorMessage,
  onClose,
  onSubmit,
}: OrganizerActionDialogProps) {
  const [reason, setReason] = useState('');
  const [outgoingEntrantPublicId, setOutgoingEntrantPublicId] = useState('');
  const [targetEntrantPublicId, setTargetEntrantPublicId] = useState('');

  const linkedEntrants = useMemo(() => uniqueLinkedEntrants(entries), [entries]);

  useEffect(() => {
    if (!intent) return;
    setReason('');
    setTargetEntrantPublicId('');
    setOutgoingEntrantPublicId(intent.kind === 'replace' ? intent.entry.members[0]?.entrantPublicId || '' : '');
  }, [intent]);

  useEffect(() => {
    if (!intent) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isPending) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [intent, isPending, onClose]);

  if (!intent) return null;

  const copy = getDialogCopy(intent);
  const excludedEntrants = new Set(
    intent.kind === 'replace'
      ? intent.entry.members.map((member) => member.entrantPublicId)
      : intent.kind === 'merge'
        ? [intent.source.entrantPublicId]
        : []
  );
  const candidates = linkedEntrants.filter((entrant) => !excludedEntrants.has(entrant.entrantPublicId));
  const requiresReason = intent.kind !== 'lock';
  const requiresTarget = intent.kind === 'replace' || intent.kind === 'merge';
  const canSubmit = (!requiresReason || reason.trim().length > 0) && (!requiresTarget || Boolean(targetEntrantPublicId));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !isPending) onClose();
    }}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="organizer-action-title"
        className="w-full max-w-lg rounded-lg border border-border bg-surface shadow-[var(--shadow-lg)]"
      >
        <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="flex min-w-0 gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              {intent.kind === 'lock' ? <LockKeyhole className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
            </div>
            <div>
              <h2 id="organizer-action-title" className="text-lg font-semibold text-text">{copy.title}</h2>
              <p className="mt-1 text-sm leading-5 text-text-muted">{copy.description}</p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            className="rounded-lg p-2 text-text-muted hover:bg-surface-elevated hover:text-text"
            disabled={isPending}
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-4 px-5 py-4">
          {intent.kind === 'replace' ? (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-text">Entrant to replace</span>
              <select
                className={selectClassName}
                value={outgoingEntrantPublicId}
                onChange={(event) => setOutgoingEntrantPublicId(event.target.value)}
              >
                {intent.entry.members.map((member) => (
                  <option key={member.entrantPublicId} value={member.entrantPublicId}>{member.displayName}</option>
                ))}
              </select>
            </label>
          ) : null}

          {requiresTarget ? (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-text">
                {intent.kind === 'merge' ? 'Keep this linked player' : 'Replacement player'}
              </span>
              <select
                className={selectClassName}
                value={targetEntrantPublicId}
                onChange={(event) => setTargetEntrantPublicId(event.target.value)}
              >
                <option value="">Select a player</option>
                {candidates.map((entrant) => (
                  <option key={entrant.entrantPublicId} value={entrant.entrantPublicId}>
                    {entrant.displayName}{entrant.levelLabel ? ` · ${entrant.levelLabel}` : ''}
                  </option>
                ))}
              </select>
              {candidates.length === 0 ? (
                <p className="text-xs text-text-muted">No eligible linked players are available in this competition.</p>
              ) : null}
            </label>
          ) : null}

          {intent.kind === 'lock' ? (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-text">
              This freezes the entrant field. The server will still verify registration closure, pending entries, division minimums, and capacity before locking.
            </div>
          ) : null}

          <Textarea
            label={intent.kind === 'lock' ? 'Internal note (optional)' : 'Reason'}
            value={reason}
            maxLength={500}
            placeholder={copy.placeholder}
            onChange={(event) => setReason(event.target.value)}
            hint={`${reason.length}/500`}
          />

          {errorMessage ? (
            <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700">
              {errorMessage}
            </div>
          ) : null}
        </div>

        <footer className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <Button variant="ghost" disabled={isPending} onClick={onClose}>Cancel</Button>
          <Button
            variant={intent.kind === 'decline' || intent.kind === 'withdraw' ? 'danger' : 'primary'}
            isLoading={isPending}
            disabled={!canSubmit}
            onClick={() => onSubmit({
              reason: reason.trim(),
              outgoingEntrantPublicId: outgoingEntrantPublicId || undefined,
              targetEntrantPublicId: targetEntrantPublicId || undefined,
            })}
          >
            {copy.confirmLabel}
          </Button>
        </footer>
      </section>
    </div>
  );
}

function getDialogCopy(intent: OrganizerEntryIntent) {
  const names = intent.kind === 'lock'
    ? intent.competition.name
    : intent.entry.members.map((member) => member.displayName).join(' & ');

  switch (intent.kind) {
    case 'decline':
      return { title: `Decline ${names}`, description: 'The entrant remains in the audit history and can see the decision.', confirmLabel: 'Decline', placeholder: 'Briefly explain the decision' };
    case 'withdraw':
      return { title: `Withdraw ${names}`, description: 'Use this when an accepted or reserve entrant needs to leave the field.', confirmLabel: 'Withdraw', placeholder: 'Why is this entry being withdrawn?' };
    case 'replace':
      return { title: `Replace ${names}`, description: 'Choose an existing linked entrant in this competition. The backend checks consent and conflicts.', confirmLabel: 'Replace', placeholder: 'Why is this replacement needed?' };
    case 'merge':
      return { title: `Merge ${intent.source.displayName}`, description: 'Move the organizer placeholder into the correct linked player and retain the audit trail.', confirmLabel: 'Merge', placeholder: 'Why are these records the same player?' };
    case 'lock':
      return { title: `Lock ${names}`, description: 'Complete entrant review before generating the draw.', confirmLabel: 'Lock entrants', placeholder: 'Optional note for the organizer audit' };
  }
}
